import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";

import { AuthService } from "../../../../auth-n-routing/auth-service.service";
import { CanDriveService } from "../../../../api-handlers/can-drive-handler.service";
import { User } from "../../../../models/user";
import { VehiclesHandlerService } from "../../../../api-handlers/vehicles-handler.service";
import { Vehicle } from "../../../../models/vehicle";

@Component({
  selector: "app-manage-vehicle-auth",
  templateUrl: "./manage-vehicle-auth.component.html",
  styleUrls: ["./manage-vehicle-auth.component.css"],
})
export class ManageVehicleAuthComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  parkId: number;
  parkName: string;

  activeVehicles: Vehicle[] = [];
  foundUsers: User[] = [];

  addVehicleAuthModalRef: BsModalRef;
  errorMessage = "";
  isLoading = false;
  isSubmitted = false;

  vehicleAuthForm: FormGroup = this.formBuilder.group({
    plate: ["", [Validators.required]],
    user: ["", [Validators.required]],
  });

  platesFilteredOptions: Observable<any>;
  usersFilteredOptions: Observable<any>;

  vehicleAuthsDataSource: MatTableDataSource<Object>;

  brandsDisplayedColumns: string[] = [
    "cdr_id",
    "cdr_vehicle_plate",
    "cdr_user_name",
    "cdr_created_at",
    "actions",
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private canDriveService: CanDriveService,
    private formBuilder: FormBuilder,
    private modalService: BsModalService,
    private snackBar: MatSnackBar,
    private vehiclesService: VehiclesHandlerService
  ) {}

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe((params) => {
      this.parkId = Number(params.get("parkId"));
      this.parkName = decodeURI(params.get("parkName"));

      this.platesFilteredOptions = this.vehicleAuthForm.valueChanges.pipe(
        startWith(""),
        map((value) => this.filterVehicles(value.plate || ""))
      );

      this.usersFilteredOptions = this.vehicleAuthForm.valueChanges.pipe(
        startWith(""),
        map((value) =>
          this.filterUsers(
            value.name || value.surname || value.cod_fiscale || ""
          )
        )
      );

      (async () => {
        try {
          const vehiclesRes: Vehicle[] = await this.vehiclesService.getByParkID(
            this.parkId + ""
          );

          this.activeVehicles = vehiclesRes;

          await this.fetchCanDrives();
        } catch (error) {
          this.snackBar.open(error.messsage, "Chiuda");
        }
      })();
    });
  }

  fetchCanDrives = async () => {
    try {
      const getCanDriveRes = await this.canDriveService.getCanDriveByParkID(
        this.parkId
      );

      this.vehicleAuthsDataSource = new MatTableDataSource(
        getCanDriveRes as any
      );
      setTimeout(() => {
        this.vehicleAuthsDataSource.paginator = this.paginator;

        this.vehicleAuthsDataSource.filterPredicate = (
          data,
          filter: string
        ) => {
          const accumulator = (currentTerm, key) => {
            return this.nestedFilterCheck(currentTerm, data, key);
          };
          const dataStr = Object.keys(data)
            .reduce(accumulator, "")
            .toLowerCase();
          const transformedFilter = filter.trim().toLowerCase();
          return dataStr.indexOf(transformedFilter) !== -1;
        };
      });
    } catch (error) {
      this.snackBar.open(error.messsage, "Chiuda");
    }
  };

  private filterVehicles(value: string) {
    const filterValue = value.toLowerCase();

    return this.activeVehicles.filter((vehicle) =>
      vehicle.plate.toLowerCase().includes(filterValue)
    );
  }

  private filterUsers(value: string) {
    const filterValue = value.toLowerCase();

    return this.foundUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(filterValue) ||
        user.surname.toLowerCase().includes(filterValue) ||
        user.cod_fiscale.toLowerCase().includes(filterValue)
    );
  }

  async checkUsers(searchValue) {
    try {
      const foundUsersRes = await this.authService.findUser(searchValue);
      this.foundUsers = foundUsersRes;
    } catch (error) {
      this.errorMessage = error.message;
    }
  }

  openAddVehicleAuthModal(template: TemplateRef<any>) {
    this.vehicleAuthForm.reset();

    this.addVehicleAuthModalRef = this.modalService.show(template, {
      ignoreBackdropClick: true,
      keyboard: false,
    });

    this.addVehicleAuthModalRef.onHide.subscribe(this.fetchCanDrives);
  }

  get getVehicleAuthFormControls() {
    return this.vehicleAuthForm.controls;
  }

  async onAddVehicleAuthSubmit() {
    this.errorMessage = "";
    this.isSubmitted = true;
    this.isLoading = true;

    try {
      const selectedPlate = this.getVehicleAuthFormControls.plate.value;
      const selectedUser = this.getVehicleAuthFormControls.user.value;

      const foundVehicle = this.activeVehicles.find(
        (v) => v.plate === selectedPlate
      );

      const foundUser = this.foundUsers.find(
        (u) => `${u.name} ${u.surname} ${u.cod_fiscale}` === selectedUser
      );

      if (!foundVehicle) {
        throw new Error("Veicolo non esiste");
      }

      if (!foundUser) {
        throw new Error("Utente non esiste");
      }

      if (this.vehicleAuthForm.invalid) {
        return;
      }

      await this.canDriveService.createCanDrive(foundUser.id, foundVehicle.id);

      this.vehicleAuthForm.reset();
      this.snackBar.open(
        `Utente ${foundUser.name} ${foundUser.surname} aggiunto come priorità su veicolo ${foundVehicle.plate}`,
        undefined,
        {
          duration: 7000,
        }
      );
      this.addVehicleAuthModalRef.hide();
    } catch (error) {
      this.errorMessage = error.message;
    } finally {
      this.isSubmitted = false;
      this.isLoading = false;
    }
  }

  applyVehicleSearchFilter(filterVal) {
    this.vehicleAuthsDataSource.filter = filterVal.trim().toLowerCase();
  }

  async onStateSwitch(rowData) {
    try {
      const stateToSend = rowData?.canDrives.cdr_closed_at ? 0 : 1;

      await this.canDriveService.updateCanDrive(
        rowData?.canDrives.cdr_id,
        stateToSend
      );

      await this.fetchCanDrives();
    } catch (error) {
      this.snackBar.open(error.message, "Chiuda");
    }
  }

  nestedFilterCheck(search, data, key) {
    if (typeof data[key] === "object") {
      for (const k in data[key]) {
        if (data[key][k] !== null) {
          search = this.nestedFilterCheck(search, data[key], k);
        }
      }
    } else {
      search += data[key];
    }
    return search;
  }
}
